import {type ExtendedOptions, UI} from "../../../stem-core/ui/UIBase";
import {Subscription} from "../../../client/state/SubscriptionStore";
import {MerchantReportType} from "../../../client/state/merchant/MerchantReportStore";
import {DashboardTablePage, MakeEmailEntry, DashboardTableColumnManager} from "../../common/DashboardTablePage";
import {
    AggregateDataViewManager,
    AnalyticsAnnotation,
    Annotation, ExtraColumnAnnotation,
    GroupByField
} from "../../common/AggregateDataViewManager";
import {MerchantUser} from "../../../client/state/merchant/MerchantUserStore";
import {Messages} from "../../../blinkpay/Messages";
import {Theme} from "../../../stem-core/ui/style/Theme";
import {Money} from "../../../stem-core/localization/Money.js";
import {BlinkInputField} from "../../common/Input";
import {StemDate} from "../../../stem-core/time/Date";
import {InfoBlockGroup} from "../../common/InfoBlock";
import {CENTER_COLUMN, LEFT_COLUMN, MONEY_COLUMN} from "../../common/theme/TableStyle";
import {CollapsiblePanel} from "../../../stem-core/ui/collapsible/CollapsiblePanel";
import {SubscriptionOffersMultiselectInput} from "../../common/input/SubscriptionCoverageMultiselectInput";
import {DashboardLabel} from "../../common/DashboardLabel";
import {autoredraw} from "../../../stem-core/decorators/AutoRedraw";
import {Level} from "../../../stem-core/ui/Constants";
import {
    DateRangeInput,
    DateRangeInterval,
    DefaultMerchantDateRangeIntervals,
    MakeMerchantAllTimeInterval
} from "../../ui/input/DateRangeInput";
import {SubscriptionCoverage} from "../../../client/state/SubscriptionCoverageStore";
import {SubscriptionOffer} from "../../../blinkpay/State";
import {PANEL_TYPE} from "../../../blink-sdk/Constants";
import {MakeInfoElement} from "../../common/InfoTooltip";
import {MessageElement} from "../../../blinkpay/widget/ui/MessageElement";
import {BillingPlan} from "../../../client/state/SubscriptionOfferStore";
import {AnalyticsEventType} from "../../../blink-sdk/utils/AnalyticsClient";
import {UserSearchInput} from "../../ui/input/UserSearchInput.jsx";
import {JourneysFilter} from "../../common/JourneysFilter.jsx";
import {RadioButtonsInput} from "../../../core/ui/input/radio/RadioButtonsInput.jsx";
import {formatPercentFromRatio} from "../../../stem-core/base/Formatting.js";
import type {SubscriptionDiscount} from "../../../client/state/SubscriptionDiscountStore";
import type {RecurringPaymentStoreObject} from "../../../client/state/RecurringPaymentStoreObject";
import type {MoneyLike} from "../../../stem-core/localization/Money";


function getDiscountEntry(subscriptionDiscount: SubscriptionDiscount | null) {
    // TODO replace inline style with class-based
    const discountContainerStyle = {
        display: "flex",
        alignItems: "center",
        height: 16,
        fontSize: 12,
        opacity: Theme.props.BASE_DISABLED_OPACITY,
    };

    if (!subscriptionDiscount) {
        return null;
    }

    const infoText = () => <MessageElement message={Messages.discountInfo} args={[subscriptionDiscount.code, subscriptionDiscount.getDiscountValue()]} />;

    return (
        <div style={discountContainerStyle}>
            <MessageElement message={Messages.discounted} />
            {MakeInfoElement(infoText)}
        </div>
    );
}


function renderBillingPlanTableEntry(billingPlan: BillingPlan) {
    return [
        <strong>{billingPlan.getPrice()}</strong>, `/${billingPlan.getAbbreviatedCycleDuration()}`
    ];
}


function renderSubscriptionOfferTableEntry(subscription: Subscription) {
    return [
        <div>{subscription.coverage.name} ({renderBillingPlanTableEntry(subscription.billingPlan)})
        </div>,
        getDiscountEntry(subscription.getDiscount()),
    ];
}

interface RecurringPaymentStatusLabelOptions {
    recurringPayment?: RecurringPaymentStoreObject;
}

// TODO rethink this: We want to know if it's active or finished first of all
@autoredraw
export class RecurringPaymentStatusLabel extends DashboardLabel {
    // DashboardLabel fixes its own options type and doesn't forward a type argument
    declare options: ExtendedOptions<DashboardLabel, RecurringPaymentStatusLabelOptions>;

    getRecurringPaymentOptions(recurringPayment: RecurringPaymentStoreObject) {
        if (recurringPayment.isCanceled() || recurringPayment.isFinished()) {
            return {label: "Canceled", level: Level.WARNING};
        }

        if (recurringPayment.isUnpaid()) {
            return {label: "Payment failed", level: Level.ERROR};
        }

        return {label: "Active"};
    }

    getDefaultOptions(options?: this["options"]) {
        return this.getRecurringPaymentOptions(options.recurringPayment);
    }

    // TODO @Mihai how to properly implement this with autoredraw and pass-through like functionality?
    redraw() {
        Object.assign(this.options, this.getDefaultOptions(this.options));
        super.redraw();
    }
}


export class MerchantSubscribersPage extends DashboardTablePage<Subscription> {
    declare requiresDeliveryFilter: RadioButtonsInput;
    declare sourceFilter: RadioButtonsInput;
    declare subscriptionOffersFilter: SubscriptionOffersMultiselectInput;
    declare subscriptionTypeFilter: RadioButtonsInput;

    declare activeUntilDateRangeInput: DateRangeInput;
    declare createdAtDateRangeInput: DateRangeInput;
    declare journeysFilter: JourneysFilter;
    declare userSearchInput: UserSearchInput;

    columnManager: DashboardTableColumnManager<Subscription> = new DashboardTableColumnManager<Subscription>("subscribers-table-columns", [
        ["Email", subscription => MakeEmailEntry(MerchantUser.getFromObject(subscription)), LEFT_COLUMN],
        ["Name", subscription => MerchantUser.getFromObject(subscription)?.getName(), LEFT_COLUMN],
        ["Plan", subscription => renderSubscriptionOfferTableEntry(subscription), CENTER_COLUMN],
        ["Offer", subscription => subscription.recurringOffer?.title || "(unnamed)", CENTER_COLUMN],
        ["Created At", subscription => subscription.createdAt, CENTER_COLUMN],
        ["Paid Until", subscription => subscription.getPaidUntil(), CENTER_COLUMN],
        ["Status", subscription => <RecurringPaymentStatusLabel recurringPayment={subscription} />, CENTER_COLUMN],
    ], {tablePage: this});
    groupsManager = new AggregateDataViewManager([
        new AnalyticsAnnotation("Flow starts", "flowStarts", AnalyticsEventType.FLOW_START.key, {
            flowType: PANEL_TYPE.subscribe,
        }),
        new ExtraColumnAnnotation("Conversion rate", entry => {
            if (!entry.flowStarts) {
                return "N/A";
            }
            return formatPercentFromRatio(Math.min(entry.count, entry.flowStarts + 1), entry.flowStarts);
        }),
        new Annotation("Total amount", "total_amount", "sum", "amount", value => new Money(value, this.getCurrency()), MONEY_COLUMN),
        new Annotation("EMR", "estimated_monthly_revenue", "sum", "estimated_monthly_revenue", value => new Money(value, this.getCurrency()), MONEY_COLUMN),
    ], [
        new GroupByField("Subscription Coverage", "coverage_id", value => SubscriptionCoverage.get(value)?.name),
        new GroupByField("Subscription Offer", "recurring_offer_id", value => SubscriptionOffer.get(value)?.title || "(unnamed)"),
        new GroupByField("Billing Plan", "billing_plan_id", value => renderBillingPlanTableEntry(BillingPlan.get(value))),
    ]);

    getDefaultOptions() {
        return {
            title: "Subscribers",
            description: "All current or past subscribers. Users can appear multiple times, " +
                "if they interrupt their service and resubscribe for instance.",
            ...super.getDefaultOptions(),
            store: Subscription,
            endpoint: "/merchant/get_subscriptions",
            reportType: MerchantReportType.SUBSCRIPTIONS,
        }
    }

    getFilterSection() {
        const merchant = this.getMerchant();

        merchant.createdAt = new StemDate(2010, 0, 1);

        const {subscriptionOffersFilter} = this;
        let coveragePanelFilter = "Filter by subscription type";
        if (subscriptionOffersFilter == null || subscriptionOffersFilter.allSelected()) {
            coveragePanelFilter += " (All Selected)"
        } else {
            coveragePanelFilter += " (Active filter)"
        }

        return [
            <div>
                <UserSearchInput
                    ref="userSearchInput"
                    onChange={() => this.applyFilters()}
                />

                <BlinkInputField label="Status type">
                    <RadioButtonsInput
                        values={["Active", "Canceled", "Failed", "All"]}
                        initialValue="Active"
                        onChange={() => this.applyFilters()}
                        ref="subscriptionTypeFilter"
                    />
                </BlinkInputField>

                <BlinkInputField label="Source">
                    <RadioButtonsInput
                        values={["All", "Made by user", "Made by staff", "Import"]}
                        initialValue="All"
                        onChange={() => this.applyFilters()}
                        ref="sourceFilter"
                    />
                </BlinkInputField>

                <BlinkInputField label="Requires Delivery">
                    <RadioButtonsInput
                        values={["Yes", "No", "All"]}
                        initialValue="All"
                        onChange={() => this.applyFilters()}
                        ref="requiresDeliveryFilter"
                    />
                </BlinkInputField>

                <BlinkInputField label="Created at">
                    <DateRangeInput
                        ref="createdAtDateRangeInput"
                        style={{maxWidth: 350, display: "inline-block"}}
                        options={DefaultMerchantDateRangeIntervals(merchant)}
                        selectedInterval={DateRangeInterval.LAST_30_DAYS}
                        onChange={() => this.applyFilters()}
                    />
                </BlinkInputField>

                <BlinkInputField label="Active until">
                    <DateRangeInput
                        ref="activeUntilDateRangeInput"
                        style={{maxWidth: 350, display: "inline-block"}}
                        options={DefaultMerchantDateRangeIntervals(merchant)}
                        selectedInterval={MakeMerchantAllTimeInterval(merchant)}
                        onChange={() => this.applyFilters()}
                    />
                </BlinkInputField>

            </div>,
            <div>
                <CollapsiblePanel collapsed={true} title="Filter by active journeys">
                    <JourneysFilter ref="journeysFilter" merchant={merchant} onChange={() => this.applyFilters()} />
                </CollapsiblePanel>
            </div>,
            <div style={{marginTop: 16}}>
                <CollapsiblePanel
                    collapsed={true}
                    title={coveragePanelFilter}
                >
                    <SubscriptionOffersMultiselectInput
                        merchant={merchant}
                        onChange={() => this.applyFilters()}
                        includeInactive={true}
                        ref="subscriptionOffersFilter"
                    />
                </CollapsiblePanel>
            </div>
        ]
    }

    getFilters() {
        const subscriptionType = this.subscriptionTypeFilter.getActive();
        const requiresDelivery = this.requiresDeliveryFilter.getActive();
        let status = null;

        if (subscriptionType == "Failed") {
            status = "unpaid";
        }

        if (subscriptionType == "Canceled") {
            status = "canceled";
        }

        let source = null;
        const subscriptionSource = this.sourceFilter.getActive();
        if (subscriptionSource == "Made by user") {
            source = ["user"];
        }
        if (subscriptionSource == "Made by staff") {
            source = ["merchant", "merchant_auto"];
        }
        if (subscriptionSource == "Import") {
            source = ["external"];
        }

        const subscriptionOfferIds = !this.subscriptionOffersFilter.allSelected() ? this.subscriptionOffersFilter.getSelectedSubscriptionOfferIds() : null;

        return {
            userFilter: this.userSearchInput.getValue(),
            status: status,
            source: source,
            createdAt: this.createdAtDateRangeInput.valueOf(),
            activeUntil: this.activeUntilDateRangeInput.valueOf(),
            activeAt: (subscriptionType == "Active") ? StemDate.now() : null,
            requiresDelivery: requiresDelivery != "All" ? (requiresDelivery == "Yes") : null,
            subscriptionOfferIds,
            metadata: this.columnManager.getMetadataFilters(),
            ...this.journeysFilter?.getValue(),
        }
    }

    // From query_merchant_subscriptions_paginated with include_summary
    summaryContent(summaryLastResponse: {count: number; estimatedMonthlyRevenue: MoneyLike; distribution: {all: number; active: number; failed: number; canceled: number}}) {
        const {count, estimatedMonthlyRevenue, distribution} = summaryLastResponse;

        return [
            <InfoBlockGroup label="Overview" entries={[
                ["subscriptions", count],
                ["monthly revenue", new Money(estimatedMonthlyRevenue)],
            ]} />,
            <InfoBlockGroup label="Breakdown" entries={[
                ["active", distribution.active],
                ["canceled", distribution.canceled],
                ["failed", distribution.failed],
            ]} />,
        ];
    }
}
